import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import type {
  DragMatchingImageElement,
  DragMatchingTextElement,
  FillBlanksElement,
  ImageChoiceElement,
  ImageElement,
  OddImageOutElement,
  OddTextOutElement,
  SpeakingElement,
  SpellingElement,
} from "@ai-teach/types";
import { useEffect, useRef, useState } from "react";
import { BorderSettings } from "./BorderSettings";

interface BorderToolbarProps {
  element:
    | ImageElement
    | ImageChoiceElement
    | DragMatchingTextElement
    | DragMatchingImageElement
    | OddTextOutElement
    | OddImageOutElement
    | FillBlanksElement
    | SpellingElement
    | SpeakingElement;
}

export function BorderToolbar({ element }: BorderToolbarProps) {
  const dispatch = useAppDispatch();
  const [localBgColor, setLocalBgColor] = useState(element.backgroundColor ?? "#ffffff");
  const bgColorTimer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    setLocalBgColor(element.backgroundColor ?? "#ffffff");
  }, [element.backgroundColor]);

  const handleBorderLineStyle = (lineStyle: string) => {
    dispatch(
      updateElementProps({
        id: element.id,
        props: { border: { ...element.border, lineStyle } },
      })
    );
  };

  const handleBorderCornerRadius = (cornerRadius: number) => {
    dispatch(
      updateElementProps({
        id: element.id,
        props: { border: { ...element.border, cornerRadius } },
      })
    );
  };

  const handleBorderWidth = (strokeWidth: number) => {
    dispatch(
      updateElementProps({
        id: element.id,
        props: { border: { ...element.border, strokeWidth } },
      })
    );
  };

  const handleBorderColorChange = (stroke: string) => {
    dispatch(
      updateElementProps({
        id: element.id,
        props: { border: { ...element.border, stroke } },
      })
    );
  };

  const handleBackgroundColorChange = (color: string) => {
    setLocalBgColor(color);
    if (bgColorTimer.current) clearTimeout(bgColorTimer.current);
    bgColorTimer.current = setTimeout(() => {
      dispatch(
        updateElementProps({
          id: element.id,
          props: { backgroundColor: color },
        })
      );
    }, 80);
  };

  return (
    <>
      <BorderSettings
        border={element.border}
        onLineStyleChange={handleBorderLineStyle}
        onStrokeWidthChange={handleBorderWidth}
        onCornerRadiusChange={handleBorderCornerRadius}
        onColorChange={handleBorderColorChange}
      />

      {/* Background color */}
      <label className="cursor-pointer">
        <div className="relative group">
          <div
            className="w-8 h-8 rounded-lg border-2 border-gray-300 transition-all duration-200 group-hover:border-primary-400 group-hover:scale-105 group-hover:shadow-md"
            style={{ backgroundColor: localBgColor }}
          />
          <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm">
            <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-red-400 via-yellow-400 to-blue-400" />
          </div>
        </div>
        <input
          type="color"
          value={localBgColor}
          onChange={(e) => handleBackgroundColorChange(e.target.value)}
          className="absolute opacity-0 w-0 h-0 pointer-events-none"
          title="Border Color"
        />
      </label>
    </>
  );
}
